import { FC, useMemo } from 'react';
import { RiskScoreLevelEnum } from '@/api';
import cx from 'classnames';
import { SvgIcon } from '@/components/SvgIcon';
import { RiskBadgeColors } from '@/config';
import { useTranslation } from 'react-i18next';

import styles from './IndicatorBinary.module.sass';

interface IndicatorBinaryProps {
  value: RiskScoreLevelEnum;
  className?: string;
}

const IndicatorBinary: FC<IndicatorBinaryProps> = ({ value, className }) => {
  const { t } = useTranslation();
  const isHigh = useMemo(() => value === RiskScoreLevelEnum.HIGH, [value]);
  const mark = useMemo(() => (isHigh ? '>' : '<'), [value]);
  const degrees = useMemo(() => {
    const val = isHigh ? 75 : 45;
    return Math.round((val / 100) * 180);
  }, [isHigh]);

  const classes = cx(
    styles.risk,
    styles[`risk--${isHigh ? 'high' : 'low'}`],
    styles[`risk--${isHigh ? RiskBadgeColors.RED : RiskBadgeColors.GREEN}`],
    className,
  );

  return (
    <>
      <div className={classes}>
        <div className={styles.inner}>
          <div className={styles.gray}>
            <div
              className={styles.level}
              style={{ transform: `rotate(${degrees}deg)` }}
            >
              <div className={styles['level-fill']} />
            </div>
          </div>
        </div>

        <div
          className={styles['level-white']}
          style={{ transform: `rotate(${degrees}deg)` }}
        />

        <div className={styles.value}>
          <SvgIcon name="risk" className={styles.value__icon} />
          <span
            className={`${styles.value__number} ${
              isHigh
                ? styles['value__number-high']
                : styles['value__number-low']
            }`}
          >
            {mark}
            50%
          </span>
        </div>

        <div className={styles.from}>0</div>
        <div className={styles.to}>100</div>
      </div>
      <div className={styles.explanation}>
        {t('translation.light_risk_explanation')}
      </div>
    </>
  );
};

export default IndicatorBinary;
